import { FC, useMemo, useRef } from 'react';
import { Bar } from 'react-chartjs-2';
import clsx from 'clsx';
import type { ChartData, ChartOptions, Plugin, TooltipItem } from 'chart.js';
import {
  getCartesianChartOptions,
  barChartVerticalCrosshairPlugin,
  resolveDatasetColor,
} from './chartConfig';
import { createChartRenderCompletePlugin } from './createChartRenderCompletePlugin';
import type { BaseChartProps } from './types';

interface BarChartProps extends BaseChartProps {
  stacked?: boolean;
}

const BarChart: FC<BarChartProps> = ({
  labels,
  datasets,
  colors,
  height = 168,
  className,
  stacked,
  onRendered,
  disableAnimation = false,
  tooltipTitles,
}) => {
  const onRenderedRef = useRef(onRendered);
  onRenderedRef.current = onRendered;

  const chartData = useMemo<ChartData<'bar'>>(() => {
    const chartDatasets = datasets.map((dataset, index) => {
      const color = resolveDatasetColor(index, dataset.color, colors);
      return {
        label: dataset.label,
        data: dataset.data,
        backgroundColor: dataset.colors ?? color,
        borderRadius: 4,
      };
    });
    return { labels, datasets: chartDatasets };
  }, [labels, datasets, colors]);

  const options = useMemo<ChartOptions<'bar'>>(() => {
    const base = getCartesianChartOptions({
      showGridX: false,
      showGridY: true,
    });
    const animationOptions = disableAnimation
      ? { animation: false as const }
      : {};
    const pluginsWithTooltipTitle = {
      ...base.plugins,
      tooltip: {
        ...base.plugins?.tooltip,
        callbacks: {
          ...base.plugins?.tooltip?.callbacks,
          title: (items: TooltipItem<'bar'>[]) => {
            const index = items[0]?.dataIndex;
            if (index !== undefined && tooltipTitles?.[index] !== undefined) {
              return tooltipTitles[index];
            }
            const label = items[0]?.label;
            return typeof label === 'string' ? label : '';
          },
        },
      },
    } as ChartOptions<'bar'>['plugins'];

    if (!stacked) {
      return {
        ...(base as ChartOptions<'bar'>),
        ...animationOptions,
        plugins: pluginsWithTooltipTitle,
        interaction: {
          mode: 'index',
          intersect: false,
        },
        hover: {
          mode: 'index',
          intersect: false,
        },
      };
    }
    const scales = base.scales;
    return {
      ...base,
      ...animationOptions,
      plugins: pluginsWithTooltipTitle,
      interaction: {
        mode: 'index',
        intersect: false,
      },
      hover: {
        mode: 'index',
        intersect: false,
      },
      scales: {
        ...scales,
        x: { ...scales?.x, stacked: true },
        y: { ...scales?.y, stacked: true },
      },
    } as ChartOptions<'bar'>;
  }, [disableAnimation, stacked, tooltipTitles]);

  const barPlugins = useMemo((): Plugin<'bar'>[] => {
    const plugins: Plugin<'bar'>[] = [barChartVerticalCrosshairPlugin];

    if (onRendered) {
      plugins.push(
        createChartRenderCompletePlugin<'bar'>(() => {
          onRenderedRef.current?.();
        }),
      );
    }

    return plugins;
  }, [onRendered]);

  return (
    <div
      className={clsx('tw-w-full', className)}
      style={{ height: typeof height === 'number' ? `${height}px` : height }}
      data-cy="analytics-chart-bar"
    >
      <Bar data={chartData} options={options} plugins={barPlugins} />
    </div>
  );
};

export default BarChart;
